import {
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  PromptModal,
  TimePicker,
  WeekdayToggleGroup,
} from '@/components'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { TimeGraph } from '@/components_deprecated/TimeGraph/TimeGraph'
import { time } from '@/lib/time'
import { ALL_SALARY_CODES_QUERY } from '@/modules/companies/queries'
import theme from '@/theme'
import { bitmaskToWeekdays, weekdaysToBitmask } from '@/utils/time'
import { useNodes } from '@/utils/hooks/useNodes'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { format } from 'date-fns'
import floor from 'lodash/floor'
import uniqueId from 'lodash/uniqueId'
import { useId } from 'react'

const WEEKDAYS = [
  ['MO', 'monday'],
  ['TU', 'tuesday'],
  ['WE', 'wednesday'],
  ['TH', 'thursday'],
  ['FR', 'friday'],
  ['SA', 'saturday'],
  ['SU', 'sunday'],
] as const

const formatMinutes = (minutes: number) =>
  `${String(floor(minutes / 60)).padStart(2, '0')}:${String(
    minutes % 60
  ).padStart(2, '0')}`

export interface PeriodAddonForm {
  id: string
  start: Date
  end: Date
  salaryCode: string | null
  excludeIfOvertime: boolean
  override: boolean

  salaryCodeName: string

  alwaysActive: boolean

  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

interface PeriodPromptProps {
  period: PeriodAddonForm | null
  existingPeriods: PeriodAddonForm[]
  isAddon: boolean

  onSubmit: (period: PeriodAddonForm | null) => void
}

export const PeriodPrompt: React.FC<PeriodPromptProps> = ({
  period,
  existingPeriods,
  isAddon,

  onSubmit,
}) => {
  const translations = useTranslate({
    createOvertimePeriod: 'companies.create-overtime-period',
    editOvertimePeriod: 'companies.edit-overtime-period',
    createPeriodAddon: 'companies.create-period-addon',
    editPeriodAddon: 'companies.edit-period-addon',
    save: 'common.save',
    timeInterval: 'common.time-interval',
    selectWeekdays: 'common.select-week-days',
    overtimeRate: 'common.overtime-rate',
    startBeforeEnd: 'timesheets.error.start-before-end',
    requied: 'common.required',
    existingOvertimePeriod: 'companies.existing-overtime-period',
    salaryCode: 'common.salary-code',
    overlappingPeriodsAreNotAllowed:
      'companies.overlapping-periods-are-not-allowed',
    excludeIfOvertime: 'settings.exclude-if-overtime',
    overrideOtherAddons: 'salary.override-other-addons',
    alwaysActive: 'settings.period-overtime-always-active',
  })

  const {
    formValues,
    formValid,
    updateForm,
    formErrors: errors,
  } = useForm<PeriodAddonForm>({
    values: {
      id: uniqueId('overtime-period-'),
      start: time(period?.start ?? '18:00').toDate(new Date()),
      end: time(period?.end ?? '24:00').toDate(new Date()),
      salaryCode: period?.salaryCode ?? null,
      salaryCodeName: period?.salaryCodeName ?? '',
      excludeIfOvertime:
        (period as PeriodAddonForm)?.excludeIfOvertime ?? false,
      override: (period as PeriodAddonForm)?.override ?? false,
      alwaysActive: (period as PeriodAddonForm)?.alwaysActive ?? false,

      monday: period?.monday ?? false,
      tuesday: period?.tuesday ?? false,
      wednesday: period?.wednesday ?? false,
      thursday: period?.thursday ?? false,
      friday: period?.friday ?? false,
      saturday: period?.saturday ?? false,
      sunday: period?.sunday ?? false,
    },
    validators: {
      start: (value, values) => {
        if (!value) return translations.requied
        if (values.end.getHours() === 0 && values.end.getMinutes() === 0)
          return null
        if (
          (value.getHours() === values.end.getHours() &&
            value.getMinutes() === values.end.getMinutes()) ||
          values.end < value
        )
          return translations.startBeforeEnd
        return null
      },
      end: (value, values) => {
        if (!value) return translations.requied
        if (value.getHours() === 0 && value.getMinutes() === 0) return null
        if (
          (value.getHours() === values.start.getHours() &&
            value.getMinutes() === values.start.getMinutes()) ||
          value < values.start
        )
          return translations.startBeforeEnd
        return null
      },
    },
    config: {
      validateImmediately: true,
    },
  })

  const checkboxIds = {
    override: useId(),
    excludeIfOvertime: useId(),
    alwaysActive: useId(),
  }

  const { data: salaryCodesData } = useQuery(ALL_SALARY_CODES_QUERY)
  const salaryCodes = useNodes(salaryCodesData?.allSalaryCodes)

  const weekdayBitmask = weekdaysToBitmask(
    WEEKDAYS.filter(([, key]) => formValues[key]).map(([day]) => day)
  )
  const noWeekdays = weekdayBitmask === 0

  function updateWeekdays(bitmask: number) {
    const selected: string[] = bitmaskToWeekdays(bitmask)
    updateForm(
      Object.fromEntries(
        WEEKDAYS.map(([day, key]) => [key, selected.includes(day)])
      )
    )
  }

  const relevantOvertimePeriods = existingPeriods.filter(
    existingPeriod =>
      existingPeriod.id !== period?.id &&
      ((formValues.monday && existingPeriod.monday === formValues.monday) ||
        (formValues.tuesday && existingPeriod.tuesday === formValues.tuesday) ||
        (formValues.wednesday &&
          existingPeriod.wednesday === formValues.wednesday) ||
        (formValues.thursday &&
          existingPeriod.thursday === formValues.thursday) ||
        (formValues.friday && existingPeriod.friday === formValues.friday) ||
        (formValues.saturday &&
          existingPeriod.saturday === formValues.saturday) ||
        (formValues.sunday && existingPeriod.sunday === formValues.sunday))
  )

  function handleSubmit() {
    if (!formValid || noWeekdays || hasOverlappingPeriods) return
    onSubmit({
      ...formValues,
    })
  }

  const periodAddonGraphStartInMinutes = time(formValues.start).reduce(
    'minutes'
  )

  const periodAddonGraphEndInMinutes =
    time(formValues.end).reduce('minutes') === 0
      ? 24 * 60
      : time(formValues.end).reduce('minutes')

  const periodAddonGraph = {
    start: periodAddonGraphStartInMinutes,
    end: periodAddonGraphEndInMinutes,
    color: theme.colors.secondary,
    borderColor: '#00ABA7',
  }

  const relevantOvertimePeriodGraphs = relevantOvertimePeriods.map(
    periodAddon => {
      const start = time(periodAddon.start).reduce('minutes')
      const end =
        time(periodAddon.end).reduce('minutes') === 0
          ? 24 * 60
          : time(periodAddon.end).reduce('minutes')
      return {
        start: start,
        end: end,
        color: theme.colors.gray6,
        borderColor: '#9399BB',
        tooltip: (
          <div className="flex flex-col p-2 text-[13px]">
            <span className="font-semibold">
              {translations.existingOvertimePeriod}
            </span>
            <span>
              {format(periodAddon.start, 'HH:mm')} -{' '}
              {format(periodAddon.end, 'HH:mm')}
            </span>
          </div>
        ),
      }
    }
  )

  const overlappingPeriods = relevantOvertimePeriodGraphs.filter(
    relevantOvertimePeriodGraph => {
      const start = relevantOvertimePeriodGraph.start
      const end = relevantOvertimePeriodGraph.end

      return (
        (start >= periodAddonGraphStartInMinutes &&
          start < periodAddonGraphEndInMinutes) ||
        (end > periodAddonGraphStartInMinutes &&
          end <= periodAddonGraphEndInMinutes) ||
        (start <= periodAddonGraphStartInMinutes &&
          end >= periodAddonGraphEndInMinutes)
      )
    }
  )

  const hasOverlappingPeriods = overlappingPeriods.length > 0

  const createTitle = translations.createPeriodAddon
  const editTitle = translations.editPeriodAddon

  const timeError = errors.end ?? errors.start

  return (
    <PromptModal
      title={period ? editTitle : createTitle}
      submitText={translations.save}
      submitDisabled={!formValid || noWeekdays || hasOverlappingPeriods}
      cancelOnEscape
      onCancel={() => onSubmit(null)}
      onSubmit={() => handleSubmit()}
    >
      <div className="flex flex-col gap-5">
        <Field>
          <FieldLabel>{translations.salaryCode}</FieldLabel>
          <SalaryCodeCombobox
            mode="single"
            clearable
            value={formValues.salaryCode ?? ''}
            onChange={code =>
              updateForm({
                salaryCode: code || null,
                salaryCodeName:
                  salaryCodes.find(salaryCode => salaryCode.id === code)
                    ?.name ?? '',
              })
            }
          />
        </Field>

        <Field>
          <FieldLabel>{translations.selectWeekdays}</FieldLabel>
          <WeekdayToggleGroup
            className="w-full"
            value={weekdayBitmask}
            onChange={updateWeekdays}
          />
          {noWeekdays && (
            <p className="ml-1 text-sm text-red-600">{translations.requied}</p>
          )}
        </Field>

        <Field>
          <FieldLabel>{translations.timeInterval}</FieldLabel>
          <div className="flex items-center gap-3">
            <TimePicker
              value={formValues.start}
              onChange={start => updateForm({ start })}
            />
            <span className="text-stone-400">–</span>
            <TimePicker
              value={formValues.end}
              onChange={end => updateForm({ end })}
            />
          </div>
          {timeError && (
            <p className="ml-1 text-sm text-red-600">{timeError}</p>
          )}
          <TimeGraph
            includeHourMarks
            graphs={[periodAddonGraph].concat(relevantOvertimePeriodGraphs)}
          />
        </Field>

        {hasOverlappingPeriods && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900">
            {translations.overlappingPeriodsAreNotAllowed}:
            {overlappingPeriods.map((period, idx) => (
              <p key={idx}>
                {formatMinutes(period.start)} - {formatMinutes(period.end)}
              </p>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {isAddon ? (
            <>
              <CheckboxField
                id={checkboxIds.override}
                label={translations.overrideOtherAddons}
                checked={formValues.override}
                onChange={override => updateForm({ override })}
              />
              <CheckboxField
                id={checkboxIds.excludeIfOvertime}
                label={translations.excludeIfOvertime}
                checked={formValues.excludeIfOvertime}
                onChange={excludeIfOvertime =>
                  updateForm({ excludeIfOvertime })
                }
              />
            </>
          ) : (
            <CheckboxField
              id={checkboxIds.alwaysActive}
              label={translations.alwaysActive}
              checked={formValues.alwaysActive}
              onChange={alwaysActive => updateForm({ alwaysActive })}
            />
          )}
        </div>
      </div>
    </PromptModal>
  )
}

const CheckboxField: React.FC<{
  id: string
  label: string
  description?: string
  checked: boolean
  onChange(checked: boolean): void
}> = ({ id, label, description, checked, onChange }) => (
  <div className="flex items-center gap-3">
    <Checkbox
      id={id}
      size="md"
      value={checked}
      onChange={value => onChange(value === true)}
    />
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {description && <FieldDescription>{description}</FieldDescription>}
    </div>
  </div>
)
